import { Breadcrumb, BreadcrumbHint } from '@sentry/react';

import { findRequestId } from './findRequestId';

/**
 * Parses `XMLHttpRequest.getAllResponseHeaders()`. That method is used rather
 * than `getResponseHeader` because, on a cross-origin response that does not
 * expose the header, `getResponseHeader` logs "Refused to get unsafe header"
 * to the console; `getAllResponseHeaders` silently leaves it out.
 */
const getXhrResponseHeaders = (xhr?: XMLHttpRequest): Record<string, string> =>
  Object.fromEntries(
    (xhr?.getAllResponseHeaders() ?? '')
      .split('\r\n')
      .map((line) => line.split(': '))
      .filter(([name, value]) => name && value)
      .map(([name, ...value]) => [name, value.join(': ')]),
  );

const getFetchResponseHeaders = (response?: Response): Record<string, string> =>
  Object.fromEntries(response?.headers.entries() ?? []);

/**
 * Sentry `beforeBreadcrumb` adapter. Adds the API request id to xhr/fetch
 * breadcrumbs, which `breadcrumbsIntegration` records without response
 * headers. An error that is not itself an API failure (ex. a render crash
 * after a bad response) then still shows the ids of the calls before it.
 */
export const beforeBreadcrumb = (
  breadcrumb: Breadcrumb,
  hint?: BreadcrumbHint,
): Breadcrumb => {
  if (breadcrumb.type !== 'http') {
    return breadcrumb;
  }

  try {
    const requestId = findRequestId(
      breadcrumb.category === 'xhr'
        ? getXhrResponseHeaders(hint?.xhr)
        : getFetchResponseHeaders(hint?.response),
    );

    if (requestId) {
      breadcrumb.data = { ...breadcrumb.data, requestId };
    }
  } catch {
    // Never let breadcrumb enrichment break the request it describes.
  }

  return breadcrumb;
};
